<template>
  <div
    class="plan-service-table__row"
    v-bind:class="{
      new: item.id === null,
      progress: isShowProgress,
      'plan-service-table__drag-selected-element': isDrag,
    }"
    v-bind:draggable="true"
    v-on:dragenter="onDragEnter"
    v-on:drop="onDrop"
    v-on:dragend="onDragEnd"
    v-on:dragover="onDragOver"
    v-on:dragstart="onDragStart"
  >
    <div
      data-label="Услуга"
      class="plan-service-table__unit"
      v-bind:title="categoryName">
      {{ serviceName }}
    </div>
    <template v-if="isShowProgress">
      <div v-if="isCompleted" title="Выполнено" class="plan-service-table__cell completed" />
      <div v-else class="plan-service-table__cell progress">{{ item.progress }}</div>
    </template>
    <popover-menu>
      <template v-slot:trigger="{ listenerObject }">
        <div
          class="plan-service-table__cell tooth"
          v-on="listenerObject"
          v-if="shownToothIcon" />
        <div
          class="plan-service-table__cell"
          v-on="listenerObject"
          v-else>{{teethText}}</div>
      </template>
      <teeth-select-popup
        v-on:submit="onSubmitTeeth"
        v-bind:initial-dental-parts="item.dentalParts"
        v-bind:initial-selected="item.teeth"
      />
    </popover-menu>
    <input-count
      class="plan-service-table__cell edit"
      data-label="Кол-во"
      v-bind:value="item.count"
      v-on:input="onInputCount"
    />
    <the-rub
      class="plan-service-table__cell"
      currency="руб"
      data-label="Цена"
      show-kop="auto"
      v-bind:kop="item.price"
    />
    <input-discount
      class="plan-service-table__cell discount"
      data-label="Скидка"
      v-bind:discount-type="discountType"
      v-bind:max-discount-in-kop="item.amount.toKop()"
      v-bind:value="item.discount.getValue()"
      v-on:input="onInputDiscount"
    />
    <the-rub
      class="plan-service-table__cell total"
      currency="руб"
      data-label="Итого"
      show-kop="auto"
      v-bind:kop="item.finalPrice.toKop()"
    />
    <div
      class="plan-service-table__cell"
      data-label="Удалить">
      <button
        type="button"
        class="plan-service-table__icon-delete"
        v-on:click="$emit('delete')" />
    </div>
  </div>
</template>

<script>
import { unitOptions } from '@/mixins/pricesMixin';
import InputCount from '@/components/InputCount.vue';
import PopoverMenu from '@/components/PopoverMenu.vue';
import TeethSelectPopup from '@/components/TeethSelectPopup.vue';
import TheRub from '@/components/TheRub.vue';
import InputDiscount from './InputDiscount.vue';

/**
 * @description Название события изменения данных строки.
 * @type {string}
 */
const NAME_EVENT_CHANGE = 'change';

/**
 * @description Название события завершения перетаскивания.
 * @type {string}
 */
const NAME_EVENT_DRAG_END = 'drag-end';

/**
 * @description Название события попадания перетаскиваемого элемента в допустимую цель.
 * @type {string}
 */
const NAME_EVENT_DRAG_ENTER = 'drag-enter';

/**
 * @description Название события начала перетаскивания.
 * @type {string}
 */
const NAME_EVENT_DRAG_START = 'drag-start';

/**
 * @description Название события сброса элемента в допустимую зону.
 * @type {string}
 */
const NAME_EVENT_DROP = 'drop';

/**
 * @description Примесь перетаскивания.
 */
const dragAndDropMixin = {
  methods: {
    /**
     * @description Обработать завершение перетаскивания.
     * @param {DragEvent} e
     * @returns {void}
     */
    onDragEnd(e) {
      if (e.dataTransfer.dropEffect === 'none') {
        this.$emit(NAME_EVENT_DRAG_END);
      }
    },
    /**
     * @description Обработать попадание перетаскиваемого элемента в допустимую цель.
     * @returns {void}
     */
    onDragEnter() {
      this.$emit(NAME_EVENT_DRAG_ENTER, this.item);
    },
    /**
     * @description Обработать перетаскивание элемента.
     * @param {DragEvent} e
     * @returns {void}
     */
    onDragOver(e) {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
    },
    /**
     * @description Обработать начало перетаскивания.
     * @param {DragEvent} e
     * @returns {void}
     */
    onDragStart(e) {
      e.dataTransfer.effectAllowed = 'move';
      this.$emit(NAME_EVENT_DRAG_START, this.item);
    },
    /**
     * @description Обработать сброс элемента в допустимую зону.
     * @param {DragEvent} e
     * @returns {void}
     */
    onDrop(e) {
      e.preventDefault();
      this.$emit(NAME_EVENT_DROP, this.item);
    },
  },
};

export default {
  components: {
    InputCount,
    InputDiscount,
    PopoverMenu,
    TeethSelectPopup,
    TheRub,
  },
  computed: {
    isCompleted() {
      return this.item.progress === 'completed';
    },
    /**
     * @description Показывать иконку зуба.
     * @type {boolean}
     */
    shownToothIcon() {
      return this.item.teeth.length + this.item.dentalParts.length === 0;
    },
    teethText() {
      const parts = this.item.dentalParts.map(({ title }) => title);
      const partSegments = parts.join(', ');
      return [...this.item.teeth, partSegments].filter((segment) => segment !== '').join(', ');
    },
  },
  data() {
    const service = this.item.getService();
    return {
      /**
       * @description Название категории.
       * @type {string}
       */
      categoryName: service.categoryName,
      /**
       * @description Название категории.
       * @type {string}
       */
      serviceName: service.title,
      /**
       * Доступные единицы измерения для услуги
       * @type {array}
       */
      unitOptions,
    };
  },
  methods: {
    /**
     * @description Отправить событие изменения строки.
     * @param {PlanService} value Измененная строка.
     * @returns {void}
     */
    emitChange(value) {
      this.$emit(NAME_EVENT_CHANGE, value);
    },
    onInputByVHIPolicy(value) {
      this.emitChange(this.item.setByVHIPolicy(value));
    },
    onInputCount(value) {
      this.emitChange(this.item.setCount(value));
    },
    onInputUnits(value) {
      this.emitChange(this.item.setUnits(value));
    },
    onInputDiscount(value) {
      const discount = this.item.discount.setValue(value);
      this.emitChange(this.item.setDiscount(discount));
    },
    /**
     * @description Изменить выбранные зубы.
     * @param {number[]} teeth
     * @param {string[]} dentalParts
     * @param {boolean}  toothGroupSelected
     * @returns {void}
     */
    onSubmitTeeth(teeth, dentalParts, toothGroupSelected) {
      this.emitChange(this.item.setTeeth(teeth, dentalParts));
      this.$nextTick(() => {
        const countTeeth = teeth.length < 1 ? 1 : teeth.length;
        this.onInputCount(toothGroupSelected ? 1 : countTeeth);
      });
    },
  },
  mixins: [dragAndDropMixin],
  props: {
    /**
     * @description Тип скидки.
     */
    discountType: { required: true, type: String },
    isShowProgress: { required: true, type: Boolean },
    /**
     * @description Элемент.
     * @type {PlanService}
     */
    item: { required: true, type: Object },
    isDrag: { required: true, type: Boolean },
  },
};
</script>

<style>
</style>
